import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';
import { useFeatureFlag } from '@/hooks/featureFlags';
import { Table, Button, Space, Tooltip, Card, Row, Col, Typography, Input, Select, Spin } from 'antd';
import { SearchOutlined, EyeOutlined, LineChartOutlined } from '@ant-design/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { useProductionPeriodFilter } from '@/hooks/useProductionPeriodFilter';
import { useProductionStartDay } from '@/hooks/useProductionStartDay';
import { getRecentProductionPeriods, getProductionPeriod } from '@/utils/productionPeriod';
import { ROLES } from '@/features/auth/types';
import type { TableProps, TablePaginationConfig } from 'antd';

const { Title, Text } = Typography;
const PAGE_SIZE = 8;

interface ClienteOuLead {
  id: string;
  cliente_id: number;
  nome: string;
  cpf: string;
  email: string;
  telefone: string;
  tipo: 'CLIENTE' | 'LEAD';
  ultimoContato: Date;
  status: string;
  agendamento_id?: number;
  contrato_id?: number;
}

export function VendasHubPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const timelineEnabled = useFeatureFlag('funil.timeline.view');
  const { productionPeriodFilter, setProductionPeriodFilter } = useProductionPeriodFilter(false);
  const startDay = useProductionStartDay();
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: PAGE_SIZE
  });
  const recentPeriods = getRecentProductionPeriods(6, startDay);

  const { data: resultado = { registros: [], total: 0 }, isLoading } = useQuery({
    queryKey: ['vendas-funil', profile?.tenant_id, profile?.uuid, profile?.role, searchTerm, pagination.current, productionPeriodFilter],
    queryFn: async () => {
      if (!profile?.tenant_id) return { registros: [], total: 0 };

      const offset = ((pagination.current || 1) - 1) * PAGE_SIZE;

      // Determinar quais clientes o usuário pode acessar baseado no role
      let clientesIds: Set<string> | null = null;
      const currentRole = profile?.role as any;

      if (currentRole === ROLES.LIDER_VENDAS) {
        try {
          const { data: consultorAtual } = await supabase
            .from('consultores')
            .select('supervisor_id')
            .eq('user_id', profile?.uuid || '')
            .eq('tipo', 'lider')
            .single();

          if (consultorAtual?.supervisor_id) {
            const { data: relacionadores } = await supabase
              .from('consultores')
              .select('user_id')
              .eq('supervisor_id', consultorAtual.supervisor_id)
              .eq('tipo', 'relacionador')
              .eq('status', 'ativo');

            if (relacionadores && relacionadores.length > 0) {
              const filterByUser = relacionadores.map((r: any) => r.user_id);
              if (profile?.uuid && !filterByUser.includes(profile.uuid)) {
                filterByUser.push(profile.uuid);
              }

              const { data: consultoresUser = [] } = await supabase
                .from('consultores')
                .select('id')
                .in('user_id', filterByUser)
                .eq('status', 'ativo');

              if (consultoresUser.length > 0) {
                const consultorIds = consultoresUser.map((c: any) => c.id);
                const { data: contratosDoConsultor = [] } = await supabase
                  .from('contratos')
                  .select('cliente_id')
                  .in('consultor_id', consultorIds)
                  .eq('tenant_id', profile.tenant_id);

                clientesIds = new Set(contratosDoConsultor.map((c: any) => c.cliente_id));
              }
            }
          }
        } catch (error) {
          console.error('Erro ao buscar relacionadores da equipe:', error);
        }
      } else if (currentRole === ROLES.SUPERVISOR) {
        try {
          const { getMembrosDoTimeSupervisor } = await import("@/utils/teamUtils");
          const membrosDoTime = await getMembrosDoTimeSupervisor(profile?.uuid);

          if (membrosDoTime && membrosDoTime.length > 0) {
            const { data: consultoresUser = [] } = await supabase
              .from('consultores')
              .select('*')
              .in('user_id', membrosDoTime)
              .eq('status', 'ativo');


            if (consultoresUser.length > 0) {
              const consultorIds = consultoresUser.map((c: any) => c.id);

              // Buscar clientes com CONTRATOS
              const { data: contratosDoConsultor = [] } = await supabase
                .from('contratos')
                .select('cliente_id')
                .in('consultor_id', consultorIds)
                .eq('tenant_id', profile.tenant_id);

              // Buscar clientes com AGENDAMENTOS (leads)
              // Nota: buscar agendamentos onde relacionador_id OU lider_vendas_id estão no time
              const { data: agendamentosDoTime = [] } = await supabase
                .from('agendamentos')
                .select('cliente_id')
                .eq('tenant_id', profile.tenant_id)
                .or(`relacionador_id.in.(${consultorIds.join(',')}),lider_vendas_id.in.(${consultorIds.join(',')})`);

              // Combinar IDs de contratos e agendamentos
              const clientesComContrato = new Set(contratosDoConsultor.map((c: any) => c.cliente_id));
              const clientesComAgendamento = new Set(agendamentosDoTime.map((a: any) => a.cliente_id).filter(Boolean));

              console.log('[VendasHubPage SUPERVISOR] Clientes com contrato:', clientesComContrato.size);
              console.log('[VendasHubPage SUPERVISOR] Clientes com agendamento:', clientesComAgendamento.size);

              // Union dos dois sets
              clientesIds = new Set([...clientesComContrato, ...clientesComAgendamento]);
              console.log('[VendasHubPage SUPERVISOR] Total de clientes (contratos + agendamentos):', Array.from(clientesIds));
            }
          }
        } catch (error) {
          console.error('Erro ao buscar membros do time:', error);
        }
      } else if (currentRole === ROLES.USER || currentRole === 'VENDEDOR') {
        try {
          const { data: consultoresUser = [] } = await supabase
            .from('consultores')
            .select('id')
            .eq('user_id', profile?.uuid || '')
            .eq('status', 'ativo');

          if (consultoresUser.length > 0) {
            const consultorIds = consultoresUser.map((c: any) => c.id);
            const { data: contratosDoConsultor = [] } = await supabase
              .from('contratos')
              .select('cliente_id')
              .in('consultor_id', consultorIds)
              .eq('tenant_id', profile.tenant_id);

            clientesIds = new Set(contratosDoConsultor.map((c: any) => c.cliente_id));
          }
        } catch (error) {
          console.error('Erro ao buscar clientes do usuário:', error);
        }
      }

      // Buscar dados completos de clientes
      let clientesQuery = supabase
        .from('clientes')
        .select('id, nome, cpf, email, tel1, status')
        .eq('tenant_id', profile.tenant_id);

      if (clientesIds && clientesIds.size > 0) {
        const clientesIdArray = Array.from(clientesIds);
        clientesQuery = clientesQuery.in('id', clientesIdArray);
      }

      if (searchTerm) {
        clientesQuery = clientesQuery.or(`nome.ilike.%${searchTerm}%,cpf.ilike.%${searchTerm}%`);
      }

      const { data: clientesData = [] } = await clientesQuery;

      if (!clientesData) {
        return { registros: [], total: 0 };
      }

      // Buscar contratos de todos os clientes (para data e status)
      const { data: contratos = [] } = clientesData.length > 0
        ? await supabase
            .from('contratos')
            .select('id, cliente_id, created_at, status')
            .in('cliente_id', clientesData.map(c => c.id))
            .eq('tenant_id', profile.tenant_id)
            .order('created_at', { ascending: false })
        : { data: [] };

      // Buscar agendamentos de todos os clientes (para data e status)
      const { data: agendamentos = [] } = clientesData.length > 0
        ? await supabase
            .from('agendamentos')
            .select('id, cliente_id, data_agendamento, status')
            .in('cliente_id', clientesData.map(c => c.id))
            .eq('tenant_id', profile.tenant_id)
            .order('data_agendamento', { ascending: false })
        : { data: [] };

      // Debug: verificar quantos clientes foram retornados
      console.log('[VendasHubPage] clientesData.length:', clientesData.length);
      console.log('[VendasHubPage] contratos.length:', contratos.length);
      console.log('[VendasHubPage] agendamentos.length:', agendamentos.length);

      // Combinar clientes com contratos e agendamentos
      const registros: ClienteOuLead[] = clientesData.map((cliente: any) => {
        const clienteContratos = contratos.filter((c: any) => c.cliente_id === cliente.id);
        const clienteAgendamentos = agendamentos.filter((a: any) => a.cliente_id === cliente.id);

        // Determinar tipo, data e status baseado em contrato vs agendamento
        let tipo: 'CLIENTE' | 'LEAD' = 'LEAD';
        let ultimoContato = new Date(cliente.created_at || 0);
        let status = cliente.status || 'LEAD';
        let contrato_id: number | undefined;
        let agendamento_id: number | undefined;

        // Verificar qual é mais recente: contrato ou agendamento
        const contratoMaisRecente = clienteContratos.length > 0 ? clienteContratos[0] : null;
        const agendamentoMaisRecente = clienteAgendamentos.length > 0 ? clienteAgendamentos[0] : null;

        const dataContrato = contratoMaisRecente ? new Date(contratoMaisRecente.created_at) : null;
        const dataAgendamento = agendamentoMaisRecente ? new Date(agendamentoMaisRecente.data_agendamento) : null;

        // Determinar qual é mais recente para saber a data do último contato
        if (dataContrato && dataAgendamento) {
          ultimoContato = dataContrato > dataAgendamento ? dataContrato : dataAgendamento;
        } else if (dataContrato) {
          ultimoContato = dataContrato;
        } else if (dataAgendamento) {
          ultimoContato = dataAgendamento;
        }

        // Se tem contrato, é cliente - e status vem do contrato
        if (contratoMaisRecente) {
          tipo = 'CLIENTE';
          status = contratoMaisRecente.status;
          contrato_id = contratoMaisRecente.id;
        }

        // Se tem agendamento, ele é um lead - status vem do agendamento
        if (agendamentoMaisRecente) {
          agendamento_id = agendamentoMaisRecente.id;
          // Só muda tipo para LEAD se não tem contrato
          if (!contratoMaisRecente) {
            tipo = 'LEAD';
            status = agendamentoMaisRecente.status;
          }
        }

        return {
          id: `${cliente.id}-${tipo}`,
          cliente_id: cliente.id,
          nome: cliente.nome,
          cpf: cliente.cpf,
          email: cliente.email,
          telefone: cliente.tel1 || '',
          tipo,
          ultimoContato,
          status,
          contrato_id,
          agendamento_id
        };
      });

      console.log('[VendasHubPage] registros criados:', registros.length);

      // Filtrar por período de produção se aplicável
      let registrosFiltrados = registros;
      if (productionPeriodFilter) {
        const period = getProductionPeriod(productionPeriodFilter.month, productionPeriodFilter.year, startDay);
        const startDate = new Date(period.startDate);
        const endDate = new Date(period.endDate);

        registrosFiltrados = registros.filter(registro => {
          const ultimoContatoDate = new Date(registro.ultimoContato);
          return ultimoContatoDate >= startDate && ultimoContatoDate <= endDate;
        });
      }

      // Ordenar por data mais recente
      registrosFiltrados.sort((a, b) => b.ultimoContato.getTime() - a.ultimoContato.getTime());

      // Paginar
      const totalRegistros = registrosFiltrados.length;
      const registrosPaginados = registrosFiltrados.slice(offset, offset + PAGE_SIZE);

      return {
        registros: registrosPaginados,
        total: totalRegistros
      };
    },
    enabled: !!profile?.tenant_id,
    staleTime: 1000 * 60 * 5
  });

  const { registros, total } = resultado;

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPagination({ ...pagination, current: 1 });
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCPF = (cpf: string) => {
    if (!cpf) return '—';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('ativo') || statusLower === 'confirmado' || statusLower === 'realizado') return '#52c41a';
    if (statusLower.includes('pendente')) return '#faad14';
    if (statusLower.includes('cancelado') || statusLower === 'faltou') return '#f5222d';
    return '#1890ff';
  };

  const columns: TableProps<ClienteOuLead>['columns'] = [
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
      width: '25%'
    },
    {
      title: 'CPF',
      dataIndex: 'cpf',
      key: 'cpf',
      render: (cpf: string) => formatCPF(cpf),
      width: '15%'
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => email || '—',
      width: '20%'
    },
    {
      title: 'Telefone',
      dataIndex: 'telefone',
      key: 'telefone',
      render: (tel: string) => tel || '—',
      width: '12%'
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      render: (tipo: string) => (
        <span style={{ color: tipo === 'CLIENTE' ? '#52c41a' : '#faad14' }}>
          {tipo === 'CLIENTE' ? 'Cliente' : 'Lead'}
        </span>
      ),
      width: '10%'
    },
    {
      title: 'Último Contato',
      dataIndex: 'ultimoContato',
      key: 'ultimoContato',
      render: (date: Date) => formatDate(date),
      width: '12%'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <span style={{ color: getStatusColor(status), fontWeight: 500 }}>
          {status}
        </span>
      ),
      width: '12%'
    },
    {
      title: 'Ações',
      key: 'actions',
      width: '8%',
      render: (_: any, record: ClienteOuLead) => (
        <Space size="small">
          <Tooltip title="Ver Timeline">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/vendas/timeline/${record.cliente_id}`)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  if (!timelineEnabled) {
    return (
      <>
        <PageHeader title="Funil de Vendas" subtitle="Clientes e Leads" />
        <div className="flex-1 bg-[#f8fafc] dark:bg-[#141414] transition-colors duration-300">
          <div className="w-full max-w-[2000px] mx-auto px-3 sm:px-4 md:px-6 xl:px-8 py-6">
            <div className="rounded-[24px] bg-white/60 dark:bg-zinc-950/60 backdrop-blur-lg border border-[rgba(255,55,55,0.08)] dark:border-white/5 p-8 text-center">
              <p className="text-gray-600 dark:text-gray-400">Funil de vendas ainda não está disponível nesta versão.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Spin spinning={isLoading} tip="Carregando vendas...">
        <PageHeader
          title="Funil de Vendas"
        subtitle="Clientes e Leads"
        extra={
          <div className="flex items-center gap-3">
            <Select
              placeholder="Período de Produção"
              allowClear
              className="w-64"
              size="large"
              value={productionPeriodFilter ? `${productionPeriodFilter.month}-${productionPeriodFilter.year}` : undefined}
              onChange={(value) => {
                if (!value) {
                  setProductionPeriodFilter(null);
                } else {
                  const [month, year] = value.split('-').map(Number);
                  setProductionPeriodFilter({ month, year });
                }
              }}
              options={recentPeriods.map(period => ({
                label: period.label,
                value: `${period.month}-${period.year}`
              }))}
            />
            <button
              onClick={() => navigate('/vendas/pipeline')}
              className="px-4 py-2 rounded-[12px] bg-[linear-gradient(135deg,#FF3737_0%,#D92B2B_100%)] border-none shadow-[0_4px_12px_rgba(255,55,55,0.3)] hover:scale-[1.02] transition-all text-white font-medium text-sm"
            >
              Pipeline
            </button>
          </div>
        }
      />
      <div className="flex-1 bg-[#f8fafc] dark:bg-[#141414] transition-colors duration-300">
        <div className="w-full max-w-[2000px] mx-auto px-3 sm:px-4 md:px-6 xl:px-8 py-6">
          <Card
            className="rounded-[24px] border-[rgba(255,55,55,0.08)] dark:border-white/5 shadow-xl"
            style={{
              background: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(10px)',
              borderColor: 'rgba(255, 55, 55, 0.08)'
            }}
          >
            {/* Header com Busca */}
            <div style={{ marginBottom: '24px' }}>
              <Row justify="space-between" align="middle" gutter={16}>
                <Col flex="auto">
                  <Input
                    placeholder="Buscar por nome ou CPF..."
                    prefix={<SearchOutlined />}
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    size="large"
                    style={{ borderRadius: '12px' }}
                  />
                </Col>
                <Col>
                  {total > 0 && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Total: <strong>{total}</strong> cliente{total !== 1 ? 's' : ''}
                    </Text>
                  )}
                </Col>
              </Row>
            </div>

            {/* Table */}
            <Table<ClienteOuLead>
              columns={columns}
              dataSource={registros.map((registro: ClienteOuLead) => ({
                ...registro,
                key: registro.id
              }))}
              loading={isLoading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: total,
                showSizeChanger: false,
                showTotal: (total, range) => (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Mostrando {range[0]} até {range[1]} de {total}
                  </Text>
                ),
                onChange: (page) => {
                  setPagination({ ...pagination, current: page });
                }
              }}
              locale={{
                emptyText: searchTerm ? 'Nenhum registro encontrado com esta busca' : 'Nenhum registro disponível'
              }}
              style={{
                background: 'transparent'
              }}
              rowClassName={() => 'cursor-pointer hover:bg-slate-50/50 dark:hover:bg-zinc-800/50'}
            />
          </Card>
        </div>
      </div>
      </Spin>
    </>
  );
}

export default VendasHubPage;
